/*
 * Copyright (c) 2021 OTB Africa <https://otbafrica.com>
 *
 * Created Date: Friday, October 15th 2021, 12:01:12 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import { getProcessedAPIResponse, APICallArgs } from "lib/api";
import { logger } from "lib/logger";
import { QueryParameters } from "lib/types";
import { APIRequestState, STATUS_CODES } from "lib/api/types";
import { useAPIRequestDependencies } from "lib/api/hooks";
import { useAPIErrorHandler } from "lib/api/providers/APIErrorHandler";

/**
 *
 * @param apiServicePath The API Service to call
 * @param apiQueryParams The query parameters for the API request
 * @param filterByGlobalOrganization Whether or not we should query the endpoint by organization selected in global scope. Note: This should always be true unless you are fetching data that does not have organization set or doing a post.
 * @param makeRequestOnInitialMount Whether to make the request on an initial mount or not.
 * @param initialDataValue The initial data value i.e. the empty value representation
 * @param method The request method i.e. GET, POST or PUT
 * @returns Returns makeRequest as an effect function to be used in a useEffect hook. https://reactjs.org/docs/hooks-effect.html
 *
 * The useAPIRequest hook is a re-usable hook that encapsulates the state and functions for an API request.
 * When using this hook, jsonResponseData, isMakingAPIRequest and isAPIError for the state of the api request. But using javascript closures,
 * this state can only ever be updated by the makeRequest closure that we return for it to be called by the calling function.
 *
 * Note that since makeRequest is intended to be executed within a useEffect Hook, it returns a cleanUp function closure
 * that can be used by useEffect when it unmounts just before a new render. Hence, makeRequest will always ensure
 * that isMakingAPIRequest is true (show a loader) and isAPIError is false before any new render that makes an API Request.
 *
 * Note: We also return the state (i.e. jsonResponseData, isMakingAPIRequest and isAPIError) but we do not return the functions for
 * updating that state i.e. it does not return setJsonResponseData, setPendingState and setErrorState
 *
 * Thus, these states can only be set when the makeRequest function is called, which is exactly what we want
 * for reusability of the states that control the data displayed, whether to display a loader (isMakingAPIRequest) and
 * whether to display an error (isAPIError).
 *
 */
export interface UseAPIRequestArgs<T, PT = T, QT = T>
  extends Pick<APICallArgs<T, PT, QT>, "apiBaseURL" | "apiServicePath"> {
  makeRequestOnInitialMount: boolean;
  filterByGlobalOrganization?: boolean;
}

export interface APIMakeRequestFncArgs<T, PT = T, QT = T> extends Omit<APICallArgs<T, PT, QT>, "apiServicePath"> {
  setValidAPIResponse: (apiResponse: T) => void;
}

export const useAPIRequest = <T, PT = T, QT = T>(args: UseAPIRequestArgs<T, PT, QT>) => {
  const { apiBaseURL, apiServicePath, filterByGlobalOrganization = true, makeRequestOnInitialMount } = args;

  const [apiRequestState, setAPIRequestState] = React.useState<APIRequestState>({
    isMakingAPIRequest: makeRequestOnInitialMount,
    isSuccessfulAPIRequest: false,
    apiErrorState: { isAPIError: false, apiErrors: {} },
    apiRequestCount: 0,
    httpResponseStatusCode: null,
  });

  const apiRequestAbortControllerRef = React.useRef<AbortController>(new AbortController());

  const { handleAPIResponseCode } = useAPIErrorHandler();

  // Automatically add organization param to apiQueryParams and get other dependencies
  const { organizationQueryParams, loggedInUser } = useAPIRequestDependencies<QT>(filterByGlobalOrganization);

  const makeRequest = React.useCallback(
    (makeRequestArgs: APIMakeRequestFncArgs<T, PT, QT>) => {
      if (apiServicePath) {
        initializeNewAPIRequestState();

        const { setValidAPIResponse } = makeRequestArgs;

        const resolvedQueryParams = {
          ...makeRequestArgs.apiQueryParams,
          ...organizationQueryParams,
        } as QueryParameters<QT>;

        getProcessedAPIResponse<T, PT, QT>({
          apiBaseURL,
          apiServicePath,
          ...makeRequestArgs,
          apiQueryParams: resolvedQueryParams,
          signal: apiRequestAbortControllerRef.current.signal,
        })
          .then(({ hasErrors, data, errors, httpResponseStatusCode }) => {
            handleAPIResponseCode(httpResponseStatusCode);

            if (hasErrors) {
              // Handle API HTTP Errors i.e. 4xx, 5xx etc.
              setAPIRequestState((prevState) => ({
                ...prevState,
                httpResponseStatusCode,
                apiErrorState: {
                  isAPIError: true,
                  apiErrors: errors,
                  apiRequestCount: prevState.apiRequestCount + 1,
                },
              }));
            } else {
              setAPIRequestState((prevState) => ({
                ...prevState,
                httpResponseStatusCode,
                isSuccessfulAPIRequest: true,
                apiRequestCount: prevState.apiRequestCount + 1,
              }));

              setValidAPIResponse?.(data as T);
            }
          })
          .catch((e) => {
            // Do nothing if aborted
            if (e.name === "AbortError") return;

            // Catch errors when fetch API fails e.g. network errors, programming errors etc.
            setAPIRequestState((prevState) => ({
              ...prevState,
              apiErrorState: {
                isAPIError: true,
                apiErrors: { [e.name]: e.message },
              },
              httpResponseStatusCode: STATUS_CODES.UnreachableServerErrorCode,
            }));

            logger.error(`Error while processing response. ${e.name}:  ${e.message}`);
          })
          .finally(() => {
            setAPIRequestState((prevState) => ({
              ...prevState,
              isMakingAPIRequest: false,
            }));
          });
      }

      // Cleanup before unmounting i.e right before another render
      return makeRequestCleanUp;
    },
    // To avoid expensive deep comparisons and since apiQueryParams is not a complex object,
    // we use JSON.stringify which is an acceptable solution and actually encouraged over deep comparisons.
    // See https://github.com/facebook/react/issues/14476#issuecomment-471199055
    // Let us re-run the api request if organization or loggedInUser changes.
    // LoggedInUser is used so we can re-run the request when a user logs in after a session timeout.
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [apiServicePath, organizationQueryParams.organization, loggedInUser]
  );

  const initializeNewAPIRequestState = React.useCallback(() => {
    setAPIRequestState((prevState) => ({
      ...prevState,
      isMakingAPIRequest: true,
      isSuccessfulAPIRequest: false,
      httpResponseStatusCode: null,
      apiErrorState: { isAPIError: false, apiErrors: {} },
    }));
  }, []);

  const makeRequestCleanUp = React.useCallback(() => {
    // Do some more clean up here if needed. The cleanup should be related to releasing resources such as memory, event listeners etc. https://reactjs.org/docs/hooks-effect.html#effects-with-cleanup
  }, []);

  const { apiErrorState, ...apiRequestStateWithoutErrorInfo } = apiRequestState;

  return {
    makeRequest,
    makeRequestCleanUp,
    apiRequestAbortController: apiRequestAbortControllerRef.current, // Can be used to abort the request like apiRequestAbortControllerRef?.abort();
    ...apiErrorState,
    ...apiRequestStateWithoutErrorInfo,
  };
};
